/**
 * Pure alarm-category-tree helpers for the operations dashboard's Alarms
 * More-filters entry: building the FilterBar 'tree' filter's option shape
 * from the reference catalog, and the selection-set rules the dashboard
 * needs to turn a category or a severity rule into the set of alarm keys
 * the tree filter actually holds — the filter's value is alarm keys only;
 * categories are a UI convenience for selecting many at once, not part of
 * the wire contract (`filter[alarm]`, a CSV of Alarm.key).
 *
 * Contains:
 * - buildAlarmTreeOptions — category>alarm tree nodes for the 'tree' filter
 * - alarmKeysForCategories — category keys -> every alarm key under them
 * - categoryKeysForAlarmKeys — the inverse, alarm keys -> their category keys
 * - alertingAlarmKeys — the Alarming Digital/Static cards' selection rule
 *   (critical/high severity, excluding the offline category)
 * - sameAlarmSelection — order-independent equality for two alarm-key arrays
 *
 * Returns plain JS — no JSX.
 */

const ALERTING_SEVERITIES = new Set(['critical', 'high']);
const ALERTING_EXCLUDED_CATEGORY_KEY = 'offline';

/**
 * @param {object[]} alarmCategories - AlarmCategory catalog rows (id, key, display_name,
 *   locale_key, display_order).
 * @param {object[]} alarmDefinitions - Alarm definition catalog rows (id, key, display_name,
 *   locale_key, severity, category_id).
 * @param {{key: string, count: number}[]} alarmChartData - per-category counts for the active
 *   window/filters; a category with no matching entry renders without a count suffix.
 * @param {Function} tAlarm - alarm-namespace translator (useAlarmTranslation()).
 * @returns {{value: string, label: string, children: {value: string, label: string}[]}[]}
 *   Category nodes (sorted by display_order) with their child alarm nodes; a category with
 *   no alarm definitions is omitted so the tree never shows an empty, unselectable parent.
 */
export function buildAlarmTreeOptions(alarmCategories, alarmDefinitions, alarmChartData, tAlarm) {
  const countByCategoryKey = new Map(alarmChartData.map(c => [c.key, c.count]));
  const defsByCategoryId = new Map();
  for (const def of alarmDefinitions) {
    const list = defsByCategoryId.get(def.category_id) ?? [];
    list.push(def);
    defsByCategoryId.set(def.category_id, list);
  }

  return [...alarmCategories]
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
    .map(category => {
      const children = (defsByCategoryId.get(category.id) ?? [])
        .map(def => ({ value: def.key, label: tAlarm(def.locale_key, { defaultValue: def.display_name }) }))
        .sort((a, b) => a.label.localeCompare(b.label));
      const categoryLabel = tAlarm(category.locale_key, { defaultValue: category.display_name });
      const count = countByCategoryKey.get(category.key);
      return {
        value: String(category.id),
        label: count != null ? `${categoryLabel} (${count})` : categoryLabel,
        children,
      };
    })
    .filter(node => node.children.length > 0);
}

/**
 * @param {object[]} alarmDefinitions
 * @param {object[]} alarmCategories
 * @param {string[]} categoryKeys - AlarmCategory.key values (e.g. 'loss_of_power', 'offline').
 * @returns {string[]} every alarm key whose category is one of categoryKeys.
 */
export function alarmKeysForCategories(alarmDefinitions, alarmCategories, categoryKeys) {
  const keySet = new Set(categoryKeys);
  const categoryIds = new Set(alarmCategories.filter(cat => keySet.has(cat.key)).map(cat => cat.id));
  return alarmDefinitions.filter(def => categoryIds.has(def.category_id)).map(def => def.key);
}

/**
 * The inverse of alarmKeysForCategories — feeds surfaces (the dashboard map)
 * that still filter by category rather than by individual alarm.
 *
 * @param {object[]} alarmDefinitions
 * @param {object[]} alarmCategories
 * @param {string[]} alarmKeys - Alarm.key values.
 * @returns {string[]} the distinct category keys the given alarms belong to.
 */
export function categoryKeysForAlarmKeys(alarmDefinitions, alarmCategories, alarmKeys) {
  const keySet = new Set(alarmKeys);
  const categoryIds = new Set(alarmDefinitions.filter(def => keySet.has(def.key)).map(def => def.category_id));
  return alarmCategories.filter(cat => categoryIds.has(cat.id)).map(cat => cat.key);
}

/**
 * @param {object[]} alarmDefinitions
 * @param {object[]} alarmCategories
 * @returns {string[]} every alarm key whose severity is critical or high, excluding the
 *   offline category — the Alarming Digital/Static Alerts cards' selection rule.
 */
export function alertingAlarmKeys(alarmDefinitions, alarmCategories) {
  const excludedCategoryIds = new Set(
    alarmCategories.filter(cat => cat.key === ALERTING_EXCLUDED_CATEGORY_KEY).map(cat => cat.id),
  );
  return alarmDefinitions
    .filter(def => ALERTING_SEVERITIES.has(def.severity) && !excludedCategoryIds.has(def.category_id))
    .map(def => def.key);
}

/**
 * @param {string[]} a
 * @param {string[]} b
 * @returns {boolean} true when both arrays hold exactly the same values, order-independent.
 */
export function sameAlarmSelection(a, b) {
  if (a.length !== b.length) return false;
  const setA = new Set(a);
  return b.every(value => setA.has(value));
}
